import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, ChevronDown, Globe, Loader2, X } from "lucide-react";
import { Button } from "./Button";
import { Input } from "./input";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getAllLanguages } from "../../redux/Actions/LanguageAction";
import { Language } from "../../modules/shared/model/LanguageInterfaces";

interface LanguageDropdownProps {
    selectedLanguage?: Language | null;
    onLanguageSelect: (language: Language | null) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    multiple?: boolean;
    selectedLanguages?: Language[];
    onLanguagesSelect?: (languages: Language[]) => void;
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
    selectedLanguage,
    onLanguageSelect,
    placeholder = "Select a language...",
    className = "",
    disabled = false,
    multiple = false,
    selectedLanguages = [],
    onLanguagesSelect,
}) => {
    const dispatch = useAppDispatch();
    const { languages, loading, totalCount, hasNextPage, currentPage } = useAppSelector((state: any) => state.language || {
        languages: [],
        loading: false,
        totalCount: 0,
        hasNextPage: false,
        currentPage: 1,
    });

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [internalSelectedLanguage, setInternalSelectedLanguage] = useState<Language | null>(null);
    const [internalSelectedLanguages, setInternalSelectedLanguages] = useState<Language[]>([]);
    const [currentPageState, setCurrentPageState] = useState(1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Load languages on component mount
    useEffect(() => {
        dispatch(getAllLanguages({
            page: 1,
            limit: 20,
            search: "",
            isShowLoader: false,
        }));
    }, [dispatch]);

    // Sync with external props
    useEffect(() => {
        if (selectedLanguage !== undefined) {
            setInternalSelectedLanguage(selectedLanguage);
        }
    }, [selectedLanguage?.id]);

    useEffect(() => {
        if (selectedLanguages !== undefined) {
            setInternalSelectedLanguages(selectedLanguages);
        }
    }, [selectedLanguages?.length, selectedLanguages?.map(l => l.id).join(",")]); // Only depend on length and ids

    // Handle search with debounce
    const handleSearch = useCallback((query: string) => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            setCurrentPageState(1);
            dispatch(getAllLanguages({
                page: 1,
                limit: 20,
                search: query,
                isShowLoader: false,
            }));
        }, 300);

        setSearchTimeout(timeout);
    }, [dispatch, searchTimeout]);

    // Load more languages
    const loadMoreLanguages = useCallback(() => {
        if (hasNextPage && !loading) {
            const nextPage = currentPageState + 1;
            setCurrentPageState(nextPage);
            dispatch(getAllLanguages({
                page: nextPage,
                limit: 20,
                search: searchQuery,
                isShowLoader: false,
            }));
        }
    }, [dispatch, hasNextPage, loading, currentPageState, searchQuery]);

    // Handle language selection
    const handleLanguageSelect = useCallback((language: Language) => {
        if (multiple) {
            const isSelected = internalSelectedLanguages.some(l => l.id === language.id);
            let newSelectedLanguages;
            
            if (isSelected) {
                newSelectedLanguages = internalSelectedLanguages.filter(l => l.id !== language.id);
            } else {
                newSelectedLanguages = [...internalSelectedLanguages, language];
            }
            
            setInternalSelectedLanguages(newSelectedLanguages);
            onLanguagesSelect?.(newSelectedLanguages);
        } else {
            setInternalSelectedLanguage(language);
            onLanguageSelect(language);
            setIsOpen(false);
            setSearchQuery("");
        }
    }, [multiple, internalSelectedLanguages, onLanguagesSelect, onLanguageSelect]);

    // Handle clear selection
    const handleClear = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        e.preventDefault();
        
        // Clear search query when clearing selection
        setSearchQuery("");
        
        if (multiple) {
            setInternalSelectedLanguages([]);
            onLanguagesSelect?.([]);
        } else {
            setInternalSelectedLanguage(null);
            onLanguageSelect(null);
        }
        
        // Reload all languages after clearing search
        dispatch(getAllLanguages({
            page: 1,
            limit: 20,
            search: "",
            isShowLoader: false,
        }));
    }, [multiple, onLanguagesSelect, onLanguageSelect, dispatch]);

    // Handle dropdown toggle
    const toggleDropdown = useCallback(() => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Focus search input when opening
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [disabled, isOpen]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Handle scroll for load more
    const handleScroll = useCallback(() => {
        if (listRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = listRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 5) {
                loadMoreLanguages();
            }
        }
    }, [loadMoreLanguages]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const getDisplayText = () => {
        if (multiple) {
            if (internalSelectedLanguages.length === 0) {
                return placeholder;
            } else if (internalSelectedLanguages.length === 1) {
                return internalSelectedLanguages[0].name;
            } else {
                return `${internalSelectedLanguages.length} languages selected`;
            }
        } else {
            return internalSelectedLanguage ? internalSelectedLanguage.name : placeholder;
        }
    };

    const isLanguageSelected = (language: Language) => {
        if (multiple) {
            return internalSelectedLanguages.some(l => l.id === language.id);
        } else {
            return internalSelectedLanguage?.id === language.id;
        }
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Trigger Button - Responsive */}
            <button
                type="button"
                onClick={toggleDropdown}
                disabled={disabled}
                className={`
                    w-full flex items-center justify-between px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg text-xs sm:text-sm
                    ${disabled
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    }
                    transition-colors
                `}
            >
                <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
                    <Globe className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                    <span className="truncate text-left text-xs sm:text-sm">{getDisplayText()}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {((multiple && internalSelectedLanguages.length > 0) || (!multiple && internalSelectedLanguage)) && (
                        <span
                            onClick={handleClear}
                            className="p-0.5 sm:p-1 hover:bg-gray-200 rounded-full transition-colors cursor-pointer inline-flex items-center justify-center"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleClear(e);
                                }
                            }}
                        >
                            <X className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-gray-400" />
                        </span>
                    )}
                    <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>

            {/* Dropdown Menu - Responsive */}
            {isOpen && (
                <div className="absolute z-50 w-full sm:min-w-[280px] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 sm:max-h-80 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 sm:p-3 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                            <Input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    handleSearch(e.target.value);
                                }}
                                className="pl-8 sm:pl-10 text-xs sm:text-sm py-1.5 sm:py-2"
                            />
                        </div>
                    </div>

                    {/* Languages List */}
                    <div 
                        ref={listRef}
                        className="max-h-48 sm:max-h-60 overflow-y-auto"
                        onScroll={handleScroll}
                    >
                        {loading && languages.length === 0 ? (
                            <div className="flex items-center justify-center py-4 sm:py-8">
                                <Loader2 className="h-4 w-4 sm:h-6 sm:w-6 animate-spin text-gray-400" />
                                <span className="ml-2 text-xs sm:text-sm text-gray-500">Loading...</span>
                            </div>
                        ) : languages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-4 sm:py-8 text-gray-500">
                                <Globe className="h-6 w-6 sm:h-8 sm:w-8 text-gray-300 mb-2" />
                                <p className="text-xs sm:text-sm">No languages found</p>
                                {searchQuery && (
                                    <p className="text-xs text-gray-400">Try different term</p>
                                )}
                            </div>
                        ) : (
                            <>
                                {languages.map((language: Language) => (
                                    <button
                                        key={language.id}
                                        type="button"
                                        onClick={() => handleLanguageSelect(language)}
                                        className={`
                                            w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-3 text-left hover:bg-gray-50
                                            ${isLanguageSelected(language) ? "bg-blue-50 border-l-2 sm:border-l-4 border-blue-500" : ""}
                                            transition-colors
                                        `}
                                    >
                                        <div className="flex-shrink-0">
                                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                                <Globe className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate text-xs sm:text-sm">
                                                {language.name}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">{language.initial}</p>
                                        </div>
                                        {multiple && isLanguageSelected(language) && (
                                            <div className="flex-shrink-0">
                                                <div className="w-4 h-4 sm:w-5 sm:h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                                    <span className="text-white text-xs">✓</span>
                                                </div>
                                            </div>
                                        )}
                                    </button>
                                ))}
                                
                                {/* Load More Indicator */}
                                {loading && languages.length > 0 && (
                                    <div className="flex items-center justify-center py-2 sm:py-4">
                                        <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin text-gray-400" />
                                        <span className="ml-2 text-xs sm:text-sm text-gray-500">Loading...</span>
                                    </div>
                                )}
                                
                                {hasNextPage && !loading && (
                                    <div className="flex items-center justify-center py-2 sm:py-4">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={loadMoreLanguages}
                                            className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
                                        >
                                            Load More
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LanguageDropdown;
